/**
 * Users data fetching hooks.
 * 
 * @module hooks/useUsers
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import type { UserWithPlan, PaginatedResponse, UserQueryOptions } from '../types';

/**
 * Extended user type with additional computed fields.
 * @see CSDEV-145 - Users list: add business count + last active columns
 */
export interface UserWithPlanExtended extends UserWithPlan {
  business_count?: number;
  last_activity?: string | null;
}

/**
 * Fetches paginated users list with optional filters.
 * Includes business count and last activity for each user.
 * 
 * @param options - Query options
 * @returns Query result with paginated users
 * @see CSDEV-145 - Users list: add business count + last active columns
 */
export function useUsers(options: UserQueryOptions = {}) {
  const {
    page = 1,
    pageSize = 25,
    search,
    planId,
    planIds,
    subscriptionStatus,
    sortBy = 'created_at',
    sortOrder = 'desc',
  } = options;

  return useQuery({
    queryKey: ['users', options],
    queryFn: async (): Promise<PaginatedResponse<UserWithPlanExtended>> => {
      let query = supabase
        .from('users')
        .select('*, plans(*), businesses(count)', { count: 'exact' });

      // Apply search filter
      if (search) {
        query = query.or(`email.ilike.%${search}%,full_name.ilike.%${search}%`);
      }

      // Apply plan filter
      if (planIds && planIds.length > 0) {
        query = query.in('plan_id', planIds);
      } else if (planId) {
        query = query.eq('plan_id', planId);
      }

      // Apply subscription status filter
      if (subscriptionStatus) {
        query = query.eq('subscription_status', subscriptionStatus);
      }

      // Apply sorting
      query = query.order(sortBy, { ascending: sortOrder === 'asc' });

      // Apply pagination
      const from = (page - 1) * pageSize;
      const to = from + pageSize - 1;
      query = query.range(from, to);

      const { data, error, count } = await query;

      if (error) {
        throw new Error(`Failed to fetch users: ${error.message}`);
      }

      // Transform data to include business count
      const transformedData = (data || []).map((user: UserWithPlan & { businesses?: Array<{ count: number }> }) => ({
        ...user,
        business_count: user.businesses?.[0]?.count || 0,
        last_activity: user.updated_at, // Use updated_at as a proxy for last activity
      })) as UserWithPlanExtended[];

      return {
        data: transformedData,
        count: count || 0,
        page,
        pageSize,
        totalPages: Math.ceil((count || 0) / pageSize),
      };
    },
  });
}

/**
 * Fetches a single user by ID with their businesses.
 * 
 * @param userId - User ID
 * @returns Query result with user details
 */
export function useUser(userId: string | undefined) {
  return useQuery({
    queryKey: ['user', userId],
    queryFn: async () => {
      if (!userId) throw new Error('User ID is required');

      const { data, error } = await supabase
        .from('users')
        .select('*, plans(*)')
        .eq('id', userId)
        .single();

      if (error) {
        throw new Error(`Failed to fetch user: ${error.message}`);
      }

      return data as UserWithPlan;
    },
    enabled: !!userId,
  });
}

/**
 * Fetches businesses owned by a user.
 * 
 * @param userId - User ID
 * @returns Query result with user's businesses
 */
export function useUserBusinesses(userId: string | undefined) {
  return useQuery({
    queryKey: ['user-businesses', userId],
    queryFn: async () => {
      if (!userId) throw new Error('User ID is required');

      const { data, error } = await supabase
        .from('businesses')
        .select('*')
        .eq('profile_id', userId)
        .order('created_at', { ascending: false });

      if (error) {
        throw new Error(`Failed to fetch user businesses: ${error.message}`);
      }

      return (data || []) as Array<{
        id: string;
        business_name: string;
        profile_id: string;
        website_url: string | null;
        industry: string | null;
        prompts: string[] | null;
        created_at: string;
        updated_at: string;
      }>;
    },
    enabled: !!userId,
  });
}

/**
 * Fetches all users for filter dropdowns.
 * Returns a simplified list with id and email only.
 * 
 * @returns Query result with all users
 * @see CSDEV-143 - Reports page filter UI
 */
export function useAllUsers() {
  return useQuery({
    queryKey: ['all-users'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('users')
        .select('id, email')
        .order('email', { ascending: true });

      if (error) {
        throw new Error(`Failed to fetch users: ${error.message}`);
      }

      return (data || []) as Array<{ id: string; email: string }>;
    },
  });
}
